






























import { computed, defineComponent, PropType, ref, toRef, unref } from '@vue/composition-api';
import {
  TextSize,
  TextWeight,
  TEXT_SIZE,
  TEXT_WEIGHT,
} from '@berlin-front/common/components/elements/AppText/composable';
import AppText from '@berlin-front/common/components/elements/AppText';
import AppIcon from '@berlin-front/common/components/elements/AppIcon';
import { RawLocation } from 'vue-router/types/router';
import useCss from '@berlin-front/common/composables/useCss';
import { useLinkColorSet, ICON_SIZE_MAP, useTextIconMarginClass } from './composable';

/**
 * ## アプリ内リンク
 *
 * リンク生成用コンポーネント。
 * 遷移先URIにはrouteオブジェクト、path文字列、イベントハンドラ、外部リンクを指定できる。
 * `<app-text>`はslotに受け入れず、本コンポーネント内でスタイル適用を行う。
 */
export default defineComponent({
  name: 'AppLink',
  components: {
    AppIcon,
    AppText,
  },

  props: {
    /**
     * リンクを無効にする。
     * 注意：現段階で無効にする必要はないのでロジックは未実装。色だけ実装。
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * テキストのサイズ。
     * subtitle1 | body2(default) | caption | heading | heading6
     */
    size: {
      type: String as PropType<TextSize>,
      required: false,
      default: TEXT_SIZE.BODY2,
    },

    /**
     * テキストの太さ。
     * weight400(default) | weight700
     */
    weight: {
      type: String as PropType<TextWeight>,
      required: false,
      default: TEXT_WEIGHT.WEIGHT400,
    },

    /**
     * 遷移先URI
     *
     * - path文字列 ex. `'/about'`。
     * - routeオブジェクト ex. `{ name: 'RouteName', param: { pathParam1: 'param-value' } }`
     * - イベントハンドラ ex. `() => logout()`
     * - urlで外部リンク指定 ex. `'https://example.com'`。
     */
    to: {
      type: [String, Object, Function] as PropType<string | RawLocation | (() => void)>,
      required: true,
    },
  },

  setup(props) {
    const linkColorSet = useLinkColorSet();

    const textSize = toRef(props, 'size');
    const iconSize = ref(ICON_SIZE_MAP[textSize.value]);
    const textIconMarginClass = useTextIconMarginClass(textSize);

    // 外部リンク。
    const isExternal = computed(
      () =>
        typeof props.to === 'string' &&
        // http(s)プロトコルのurl
        /^([hH][tT]{2}[pP][sS]?:)?\/\/.*/.test(props.to),
    );

    // アプリ起動リンク。
    const isApp = computed(
      () =>
        !unref(isExternal) &&
        typeof props.to === 'string' &&
        // RFC3986によって定義されるスキーマ。アプリケーションの起動で使用する可能性がある文字列。
        /^[a-zA-Z][-+.a-zA-Z\d]*:.*/.test(props.to),
    );

    // イベントハンドラ。ログインで使用する。
    const isEventHandler = computed(() => typeof props.to === 'function');

    // normal、pressed, disabledの状態それぞれでリンク色（文字）が変わるようにする
    useCss(
      () => `
      .AppLink .AppText {
        color: ${linkColorSet.value.normal}
      }
      .AppLink .v-icon__svg {
        fill: ${linkColorSet.value.normal}
      }
      .AppLink:active .AppText {
        color: ${linkColorSet.value.pressed}
      }
      .AppLink:active .v-icon__svg {
        fill: ${linkColorSet.value.pressed}
      }
      .AppLink.AppLink--disabled .AppText {
        color: ${linkColorSet.value.disabled}
      }
      .AppLink.AppLink--disabled .v-icon__svg {
        fill: ${linkColorSet.value.disabled}
      }
      .AppLink.AppLink--disabled:active .AppText {
        color: ${linkColorSet.value.disabled}
      }
      .AppLink.AppLink--disabled:active .v-icon__svg {
        color: ${linkColorSet.value.disabled}
      }
    `,
    );
    return {
      classNames: ['AppLink', props.disabled ? 'AppLink--disabled' : ''],
      iconClassNames: ['AppLink__Icon', textIconMarginClass.value],
      iconSize,
      isExternal,
      isApp,
      isEventHandler,
    };
  },
});
